import { useCallback, useEffect, useReducer, useState } from 'react'
import { Autocomplete, Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ptBR } from '@mui/x-date-pickers/locales'

import useFetch from '../../../api/api'
import CurrencyField from '../../../components/CurrencyField';
import CurrencyFormulaField from '../../../components/CurrencyFormulaField';
import config from '../../../common/Config';

const TabTransactionForm = (props) => {

	const { type, setDisplayErrorAlert, setDisplaySuccessAlert, setMessage, setCurrentTabIndex, initialTicker } = props

	const [tickers, setTickers]					= useState([])
	const [date, setDate] 						= useState();
	const [dateError, setDateError]				= useState(false)
	const [total, setTotal]						= useState(0);
	const [highlightErrors, setHighlightErrors]	= useState(false)

	const [inputFields, dispatch]				= useReducer(reducer, [inputFieldModel(type, initialTicker)])

	const privateFetch = useFetch();

	useEffect(() => {
		privateFetch(config.apiUrl +'/api/companies/search?market='+ (type.toUpperCase() === 'STOCK' ? 'US' : 'BR'))
		.then(result => result.json())
		.then(result => {
			setTickers(result.map(r => r.ticker))
		})
	}, [type])

	useEffect(() => {
		if (initialTicker) {
			dispatch({
				type	: 'update',
				index	: 0,
				payload	: { ticker: initialTicker }
			})
		}
	}, [initialTicker])

	// Adiciona novos campos no formulario
	const handleAddFields = () => {
		dispatch({
			type 		: 'add',
			payload 	: inputFieldModel(type)
		})
	}

	// Remove campos do formulario
	const handleRemoveFields = useCallback((index) => {
		dispatch({
			type		: 'remove',
			index		: index
		})
	})

	// Atualiza dados do form
	const handleFormChange = (index, prop, value) => {
		dispatch({
			type		: 'update',
			index		: index,
			payload		: { [prop] : value },
			total		: total
		})
	}

	const handleCostCalculation = () => {
		if (!total) return 

		const totalPaid = inputFields.reduce( (acc,f) => { 
			if (f.totalPaid) acc += f.totalPaid
			return acc
		}, 0)

		dispatch({
			type		: 'updateCost',
			totalPaid	: totalPaid,
			cost		: total - totalPaid
		})
	}

	// Enviar dados para servidor
	const handleSaveTransactions = () => {
		if (!date) setDateError(true)

		if (isFormValid(inputFields) && date) {

			let bodyJson = `{
				"date" 			: `+ date + `,
				"transactions"	: `+ stringify(inputFields) +`
			}`
	
			let options = {
				method : 'POST',
				headers : {
					'content-type' : 'application/json; charset=utf-8',
				},
				body : bodyJson
			}

			privateFetch(config.apiUrl +"/api/transactions", options)
			.then((data) => data.json())
			.then((data) => { 
				if (data.status === 500) {
					setDisplaySuccessAlert(false)
					setDisplayErrorAlert(true)
					setMessage('Erro ao criar transações: '+ data.message)
				}
				else {
					setDisplaySuccessAlert(true)
					setDisplayErrorAlert(false)
					setMessage('Transações adicionadas com sucesso!')
					setCurrentTabIndex(0);
				}			
			})

			setHighlightErrors(false)
		}
		else {
			setDisplaySuccessAlert(false)
			setDisplayErrorAlert(true)
			setMessage('Erro salvar transações: preencher corretamentamete as informações.')		
			setHighlightErrors(true)
		}
	}
	
	return (
		<Paper variant='outlined' sx={{m: 1}}>
			<Grid container marginY={2} marginX={1} width={{xs: '96%', sm: '98%'}}>
				<Grid item xs={12}>
					<Stack direction={ 'row'}>
						<DatePicker 
							format="DD/MM/YYYY" 
							label="Data" 
							onChange={(value) => {
								setDate(value)
								setDateError(false)
							}} 
							localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
							slotProps={{
								textField: {
								variant: 'outlined',
								error: dateError,
								helperText: dateError && 'Uma data precisar ser preenchida!',
								},
							}}
							/>
						<Box flex={1}/>
						<CurrencyField 
							label="Total da Nota" 
							onChange={(e) => setTotal(e.target.value)} 
							onBlur={handleCostCalculation}
							adornment={type.toUpperCase() === 'STOCK' ? '$' : 'R$'}
						/>
					</Stack>
				</Grid>
				<Grid item xs={12} paddingTop={2}>
					<Stack direction={'column'} spacing={1}>
					{inputFields && inputFields.map((inputField, index)=> {
						const [isValid, errorMessage]	= inputField.isValid
						return (
							<Stack key={index} direction={'column'}>
								<Stack direction={{md:'row', xs:'column'}} spacing={1}>
									<Autocomplete
										options={tickers}
										loading={tickers.length === 0 ? true : false}
										loadingText="Carregando"
										name="ticker" 
										label="Ticker" 
										value={inputField.ticker} 
										onChange={(e, value) => handleFormChange(index, 'ticker', value)}										
										renderInput={(params) => 
											<TextField 
												{...params}
												label="Ticker"
												error={highlightErrors && !isValid}
											/>
										}
										fullWidth
									/>
									{/* TODO: Implenetar a venda. Fazer uma operacao com compra e venda na mesma nota e ver o que acontece */}
									<FormControl error={highlightErrors && !isValid} fullWidth>
										<InputLabel id="select-operation-label">Operação</InputLabel>
										<Select
											labelId="select-operation-label"
											id="select-operation"
											name="operation"
											value={inputField.operation}
											label="Operação"
											onChange={e => handleFormChange(index, 'operation', e.target.value)}
											>
											<MenuItem value={''}>Selecione</MenuItem>
											<MenuItem value={'BUY'}>Compra</MenuItem>
											<MenuItem value={'SELL'}>Venda</MenuItem>
										</Select>									
									</FormControl>
									<TextField 
										name="quantity" 
										label="Quantidade" 
										value={inputField.quantity}
										onChange={e => handleFormChange(index, 'quantity', e.target.value)}
										error={highlightErrors && !isValid}
										fullWidth
									/>
									<CurrencyFormulaField 
										name="totalPaid" 
										label="Total Pago"
										value={inputField.totalPaidFormula}
										onChange={e => {
											handleFormChange(index, 'totalPaidFormula', e.target.value)
											handleFormChange(index, 'totalPaid', e.target.calculatedValue)
										}}
										onBlur={handleCostCalculation}
										error={highlightErrors && !isValid}
										adornment={type.toUpperCase() === 'STOCK' ? '$' : 'R$'}
										fullWidth
									/>
									<CurrencyField 
										name="cost" 
										label="Custo" 
										value={inputField.cost} 
										onChange={e => handleFormChange(index, 'cost', e.target.value)}
										error={highlightErrors && !isValid}
										adornment={type.toUpperCase() === 'STOCK' ? '$' : 'R$'}
										fullWidth
									/>
									<Button variant="contained" onClick={() => handleRemoveFields(index)} fullWidth>Remover</Button>
								</Stack>
								{highlightErrors && !isValid ? <Typography variant="caption" color={'error'} paddingBottom={1}>{errorMessage}</Typography> : null }
							</Stack>
						)
					})}
					</Stack>
				</Grid>

				<Grid item xs={12} paddingTop={1}>
					<Stack direction={'row'} spacing={1}>
						<Button variant="contained" onClick={handleAddFields}>Adicionar +</Button>
						<Button variant="contained" onClick={handleSaveTransactions}>Salvar</Button>
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	)
}

const inputFieldModel = (type, ticker = null) => {
	return {
		ticker: ticker, 
		assetType: type.toUpperCase(), 
		operation: '', 
		quantity: 0, 
		totalPaid: 0, 
		cost: 0, 
		isValid: [false, '']
	}
}

const reducer = (inputFields, action) => {
	let updatedState = [...inputFields];

	switch (action.type) {
		case 'add' : 
			updatedState = [...inputFields, action.payload]; 
			break;
		
		case 'remove' : 
			updatedState.splice(action.index, 1)
			break;

		case 'update' : 
			updatedState[action.index] = {...updatedState[action.index], ...action.payload}
			updatedState[action.index].isValid = validate(updatedState[action.index])
			break;

		case 'updateCost' : 
			updatedState = updatedState.map(c => ({
				...c,
				cost	: parseFloat(action.cost * (c.totalPaid / action.totalPaid)).toFixed(2),
				isValid	: validate(c)
			}))
			break;
	}

	return updatedState	
}

const validate = (inputField) => {

	if (!inputField.ticker || inputField.ticker === '') 		return [false, 'Ticker deve ser preenchido.']
	if (!inputField.operation || inputField.operation === '' ) 	return [false, 'Selecione um tipo de operação.']
	if (!inputField.quantity )									return [false, 'Quantidade deve ser preenchida.']
	if (inputField.quantity < 0 || isNaN(inputField.quantity))	return [false, 'Quantidade inválida.']
	if (inputField.totalPaid <= 0 )								return [false, 'Total pago inválido.']
	if (inputField.cost < 0 || isNaN(inputField.cost))			return [false, 'Custo inválido.']

	return [true, 'Campos validados com sucesso!']
}

const isFormValid = (inputFields) => {
	return inputFields.reduce( (acc, f) => acc &&= f.isValid[0], true)
}

const stringify = (inputFields) => {
	return JSON.stringify(inputFields.map(f => ({
		...f,
		quantity	: Math.abs(f.quantity) * (f.operation === 'SELL' ? -1 : 1),
		totalPaid	: Math.abs(f.totalPaid) * (f.operation === 'SELL' ? -1 : 1),
		cost		: Math.abs(f.cost) * (f.operation === 'SELL' ? -1 : 1)
	})))
}

export default TabTransactionForm
